import { HttpHandlerFn, HttpEvent, HttpRequest } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { isPrimaryRequest } from './accept-language.context';

/**
 * offlineInterceptor — v2's own copy of the app-wide offline gate the legacy
 * `offline.interceptor.ts` (common/core/data-access/http-client/interceptors)
 * currently applies to EVERY request, v1 and v2 alike (no v2 skip-branch).
 * Added per docs/v2-isolation/ESCALATIONS.md E1, same reasoning as
 * {@link headersInterceptor} — v2 needs this BEFORE `common/` (which owns
 * the legacy version) can be deleted.
 *
 * Simplified from the legacy version: legacy reads a reactive
 * `NetworkStatusService.isOnline$()` (a `merge(of(navigator.onLine),
 * fromEvent(window,'online'/'offline'))` observable), then does
 * `take(1)` — which, on a fresh subscription, always resolves to that first
 * synchronous `of(navigator.onLine)` emission. A direct synchronous
 * `navigator.onLine` read at request time is therefore behaviourally
 * identical for this one-shot per-request gate (the observable's event
 * listeners only matter for OTHER, longer-lived consumers watching
 * connectivity change over time — this interceptor was never one of them).
 *
 * The thrown error shape does not need to match legacy's `ApiErrorFactory`
 * object exactly: `AppHttp`'s `toApiError` only branches on
 * `ApiError` / `TimeoutError` / `HttpErrorResponse` — anything else
 * (this plain object included) falls through to the same generic
 * "could not reach the server" `ApiError`, which is v2's actual observed
 * behaviour today (the legacy interceptor's specific "No internet
 * connection" message is never surfaced to a v2 caller).
 */
export function offlineInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (!isPrimaryRequest(req)) return next(req);

  const platformId = inject(PLATFORM_ID);
  if (!isPlatformBrowser(platformId)) return next(req); // SSR: always allow, matches legacy

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return throwError(() => ({
      status: 0,
      message: 'No internet connection',
      timestamp: new Date().toISOString(),
    }));
  }

  return next(req);
}
